import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SocialCardGenerator } from '@/components/social-cards/SocialCardGenerator';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Sparkles } from 'lucide-react';

export default function SocialCardsPage() {
  const [selectedContent, setSelectedContent] = useState<{
    title: string;
    excerpt?: string;
    image?: string;
    type: 'blog' | 'travel-byte';
  } | undefined>();

  const { data: blogPosts, isLoading: loadingBlogs } = useQuery({
    queryKey: ['blog-posts-for-cards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, excerpt, featured_image')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  const { data: travelBytes, isLoading: loadingBytes } = useQuery({
    queryKey: ['travel-bytes-for-cards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('short_content')
        .select('id, title, content, image_url')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  const handleSelectBlog = (id: string) => {
    const post = blogPosts?.find((p) => p.id === id);
    if (post) {
      setSelectedContent({
        title: post.title,
        excerpt: post.excerpt || undefined,
        image: post.featured_image || undefined,
        type: 'blog',
      });
    }
  };

  const handleSelectByte = (id: string) => {
    const byte = travelBytes?.find((b) => b.id === id);
    if (byte) {
      setSelectedContent({
        title: byte.title,
        excerpt: byte.content?.substring(0, 150) || undefined,
        image: byte.image_url || undefined,
        type: 'travel-byte',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Social Card Generator | Dashboard</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-8 h-8" />
            Social Card Generator
          </h1>
          <p className="text-muted-foreground mt-1">
            Create beautiful shareable images from your content
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Select Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="blog" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="blog">Blog Posts</TabsTrigger>
                <TabsTrigger value="travel-bytes">Travel Bytes</TabsTrigger>
                <TabsTrigger value="custom">Custom</TabsTrigger>
              </TabsList>

              <TabsContent value="blog">
                {loadingBlogs ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select onValueChange={handleSelectBlog}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a blog post..." />
                    </SelectTrigger>
                    <SelectContent>
                      {blogPosts?.map((post) => (
                        <SelectItem key={post.id} value={post.id}>
                          {post.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </TabsContent>

              <TabsContent value="travel-bytes">
                {loadingBytes ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select onValueChange={handleSelectByte}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a travel byte..." />
                    </SelectTrigger>
                    <SelectContent>
                      {travelBytes?.map((byte) => (
                        <SelectItem key={byte.id} value={byte.id}>
                          {byte.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </TabsContent>

              <TabsContent value="custom">
                <p className="text-sm text-muted-foreground">
                  Use the generator below to create a card from scratch.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <SocialCardGenerator initialContent={selectedContent} />
      </div>
    </>
  );
}
